import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Trans } from 'react-i18next';
import { toast } from 'sonner';
import {
  mapPromotionalAdError,
  useAuthSession,
  useDeletePromotionalAd,
  useReorderPromotionalAds,
  useStorePromotionalAds,
  useTranslation,
} from '@dukanti/shared';
import type {
  PromotionalAdErrorKey,
  StorePromotionalAdStoreContext,
  StorePromotionalAdWithPreview,
  TranslationKey,
} from '@dukanti/shared';
import { AlertCircle, Info, Plus } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useCurrentStore } from '@/hooks/use-current-store';
import { Button } from '@/components/ui/button';
import { PromotionalAdsList } from '@/features/promotional-ads/components/promotional-ads-list';
import { PromotionalAdsEmptyState } from '@/features/promotional-ads/components/promotional-ads-empty-state';
import { PromotionalAdsListSkeleton } from '@/features/promotional-ads/components/promotional-ads-list-skeleton';
import { DeletePromotionalAdDialog } from '@/features/promotional-ads/components/delete-promotional-ad-dialog';
import { orderPromotionalAdsForDisplay } from '@/features/promotional-ads/lib/promotional-ad-ordering';
import { MarketingTabs } from '@/features/promotional-ads/components/marketing-tabs';
import {
  usePromotionalAdsAccess,
  type PromotionalAdsAccessNotice,
} from '@/features/promotional-ads/hooks/use-promotional-ads-access';

const PAGE_CLASS = 'mx-auto w-full max-w-5xl px-4 py-6 sm:px-6 lg:px-8';
const TITLE_CLASS =
  'font-display text-foreground text-start text-[28px] font-bold leading-tight tracking-tight';

function toStoreContext(
  storeId: string | null,
  storeSlug: string | null,
): StorePromotionalAdStoreContext | null {
  if (!storeId || !storeSlug) return null;
  return { storeId, storeSlug };
}

/**
 * `/promotions`. Epic #228 P7: Ads authoring is legacy-only. A store that can
 * no longer author ads but still holds rows sees them read-only; a store with
 * none lands on Campaigns. This route owns the one gate; the state pages
 * below only take its result as props, so swapping them never remounts the
 * gate's queries (BUG-P7-1).
 */
export function PromotionsRoute() {
  const access = usePromotionalAdsAccess();
  if (access.state === 'hidden') return <Navigate to="/promotions/campaigns" replace />;
  if (access.state === 'loading') return <PromotionsLoadingPage />;
  if (access.state === 'error') return <PromotionsAccessErrorPage onRetry={access.retry} />;
  if (access.state === 'readOnly') {
    return <PromotionsReadOnlyPage notice={access.notice ?? 'legacy'} ads={access.ads} />;
  }
  return <PromotionsAuthoringPage />;
}

function PromotionsLoadingPage() {
  const { dir } = useTranslation();
  return (
    <main dir={dir} data-testid="promotions-route" aria-busy="true" className={PAGE_CLASS}>
      <MarketingTabs active="ads" accessState="loading" />
      <PromotionalAdsListSkeleton />
    </main>
  );
}

/** The gate could not load: neutral copy, no claim about the store's design. */
function PromotionsAccessErrorPage({ onRetry }: { readonly onRetry: () => void }) {
  const { t, dir } = useTranslation();
  return (
    <main dir={dir} data-testid="promotions-route" data-access="error" className={PAGE_CLASS}>
      <MarketingTabs active="ads" accessState="error" />
      <div
        role="alert"
        data-testid="promotions-route-access-error"
        className="border-border bg-card shadow-admin-card mx-auto mt-6 max-w-lg rounded-lg border p-8 text-center"
      >
        <AlertCircle className="text-destructive mx-auto mb-4 size-10" strokeWidth={1.5} />
        <h2 className="font-display text-foreground mb-2 text-xl">{t('common.error')}</h2>
        <p className="text-muted-foreground mb-5 text-sm">
          {t('admin.promotionalAds.accessLoadError' as TranslationKey)}
        </p>
        <Button type="button" variant="outline" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      </div>
    </main>
  );
}

/**
 * The `legacy` notice also says where banners are edited now (UX-P7-1):
 * Storefront, then Edit mode, with a link to `/storefront`. The tags in the
 * translation (`<storefrontLink>`, `<editMode>`) keep word order per locale.
 */
function ReadOnlyNoticeText({ notice }: { readonly notice: PromotionalAdsAccessNotice }) {
  const { t } = useTranslation();
  if (notice === 'staff') {
    return <span>{t('admin.promotionalAds.readOnlyStaffNotice' as TranslationKey)}</span>;
  }
  return (
    <span>
      <Trans
        i18nKey="admin.promotionalAds.legacyNotice"
        components={{
          storefrontLink: (
            <Link
              to="/storefront"
              data-testid="promotional-ads-readonly-storefront-link"
              className="focus-visible:ring-ring rounded-sm font-semibold underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2"
            />
          ),
          editMode: <strong className="font-semibold" />,
        }}
      />
    </span>
  );
}

interface PromotionsReadOnlyPageProps {
  readonly notice: PromotionalAdsAccessNotice;
  /** The rows the gate read (non-empty in `readOnly`); no query of its own. */
  readonly ads: readonly StorePromotionalAdWithPreview[];
}

/** Retained ads, shown without any mutation control. */
function PromotionsReadOnlyPage({ notice, ads: retained }: PromotionsReadOnlyPageProps) {
  const { t, dir } = useTranslation();
  const ads = useMemo(() => orderPromotionalAdsForDisplay(retained, null), [retained]);

  return (
    <main dir={dir} data-testid="promotions-route" data-access="readOnly" className={PAGE_CLASS}>
      <div className="mb-6">
        <MarketingTabs active="ads" accessState="readOnly" />
        <h1 className={TITLE_CLASS}>{t('admin.promotionalAds.title' as TranslationKey)}</h1>
      </div>

      <div
        role="note"
        data-testid="promotional-ads-readonly-notice"
        data-notice={notice}
        className="border-saffron/40 bg-saffron/10 text-foreground mb-5 flex items-start gap-2 rounded-lg border px-3 py-2 text-start text-sm"
      >
        <Info className="text-saffron mt-0.5 size-4 shrink-0" aria-hidden />
        <ReadOnlyNoticeText notice={notice} />
      </div>

      {ads.length > 0 ? <PromotionalAdsList ads={ads} readOnly /> : null}
    </main>
  );
}

function PromotionsAuthoringPage() {
  const { t, dir } = useTranslation();
  const navigate = useNavigate();
  const authSession = useAuthSession(supabase);
  const { storeId, storeSlug } = useCurrentStore(authSession);
  const storeContext = useMemo(
    () => toStoreContext(storeId, storeSlug ?? null),
    [storeId, storeSlug],
  );
  const adsQuery = useStorePromotionalAds(supabase, storeContext);
  const reorderAds = useReorderPromotionalAds(supabase);
  const deleteAd = useDeletePromotionalAd(supabase);

  const [deletingAd, setDeletingAd] = useState<StorePromotionalAdWithPreview | null>(null);
  const [deleteError, setDeleteError] = useState<PromotionalAdErrorKey | null>(null);
  const [pendingOrderIds, setPendingOrderIds] = useState<readonly string[] | null>(null);

  const ads = useMemo(() => {
    return orderPromotionalAdsForDisplay(adsQuery.ads, pendingOrderIds);
  }, [adsQuery.ads, pendingOrderIds]);

  useEffect(() => {
    setPendingOrderIds(null);
  }, [storeContext?.storeId]);

  function openCreate(): void {
    navigate('/promotions/new');
  }

  function openEdit(ad: StorePromotionalAdWithPreview): void {
    navigate(`/promotions/${ad.id}/edit`);
  }

  async function handleMove(
    ad: StorePromotionalAdWithPreview,
    direction: 'up' | 'down',
  ): Promise<void> {
    if (!storeContext) return;
    const currentIndex = ads.findIndex((item) => item.id === ad.id);
    const nextIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= ads.length) return;

    const next = [...ads];
    const current = next[currentIndex];
    const target = next[nextIndex];
    if (!current || !target) return;
    next[currentIndex] = target;
    next[nextIndex] = current;
    const orderedAdIds = next.map((item) => item.id);
    setPendingOrderIds(orderedAdIds);

    try {
      await reorderAds.mutateAsync({
        store_id: storeContext.storeId,
        ordered_ad_ids: orderedAdIds,
      });
      toast.success(t('admin.promotionalAds.toasts.reordered' as TranslationKey));
    } catch (err) {
      setPendingOrderIds(null);
      const mapped = mapPromotionalAdError(err);
      toast.error(t(mapped as TranslationKey));
    }
  }

  function openDelete(ad: StorePromotionalAdWithPreview): void {
    setDeletingAd(ad);
    setDeleteError(null);
  }

  function handleDeleteOpenChange(open: boolean): void {
    if (!open) {
      setDeletingAd(null);
      setDeleteError(null);
    }
  }

  function handleDeleteConfirm(): void {
    if (!deletingAd) return;
    deleteAd.mutate(
      { ad_id: deletingAd.id },
      {
        onSuccess: () => {
          toast.success(t('admin.promotionalAds.toasts.deleted' as TranslationKey));
          setPendingOrderIds(null);
          setDeletingAd(null);
          setDeleteError(null);
        },
        onError: (err: unknown) => {
          const mapped = mapPromotionalAdError(err);
          setDeleteError(mapped);
          toast.error(t(mapped as TranslationKey));
        },
      },
    );
  }

  const isLoading = adsQuery.isLoading;
  const isError = adsQuery.isError || !storeContext;
  const errorKey = !storeContext ? 'common.errors.permissionDenied' : adsQuery.errorKey;

  return (
    <main dir={dir} data-testid="promotions-route" className={PAGE_CLASS}>
      <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <div>
          <MarketingTabs active="ads" accessState="authoring" />
          <h1 className={TITLE_CLASS}>{t('admin.promotionalAds.title' as TranslationKey)}</h1>
          <p className="text-muted-foreground mt-1 max-w-2xl text-start text-sm">
            {t('admin.promotionalAds.subtitle' as TranslationKey)}
          </p>
        </div>
        <Button type="button" onClick={openCreate} disabled={!storeContext}>
          <Plus className="size-4" aria-hidden />
          <span>{t('admin.promotionalAds.addNew' as TranslationKey)}</span>
        </Button>
      </div>

      {isLoading ? <PromotionalAdsListSkeleton /> : null}

      {!isLoading && isError ? (
        <div
          role="alert"
          data-testid="promotions-route-error"
          className="border-border bg-card shadow-admin-card mx-auto max-w-lg rounded-lg border p-8 text-center"
        >
          <AlertCircle className="text-destructive mx-auto mb-4 size-10" strokeWidth={1.5} />
          <h2 className="font-display text-foreground mb-2 text-xl">{t('common.error')}</h2>
          <p className="text-muted-foreground mb-5 text-sm">
            {t((errorKey ?? 'common.errors.unknown') as TranslationKey)}
          </p>
          {storeContext ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                void adsQuery.refetch();
              }}
            >
              {t('common.retry')}
            </Button>
          ) : null}
        </div>
      ) : null}

      {!isLoading && !isError && ads.length === 0 ? (
        <PromotionalAdsEmptyState onCreate={openCreate} />
      ) : null}

      {!isLoading && !isError && ads.length > 0 ? (
        <PromotionalAdsList
          ads={ads}
          isReordering={reorderAds.isPending}
          onEdit={openEdit}
          onDelete={openDelete}
          onMove={handleMove}
        />
      ) : null}

      <DeletePromotionalAdDialog
        ad={deletingAd}
        open={deletingAd !== null}
        isDeleting={deleteAd.isPending}
        errorKey={deleteError}
        onOpenChange={handleDeleteOpenChange}
        onConfirm={handleDeleteConfirm}
      />
    </main>
  );
}
